import React, { useMemo } from "react";
import styled from "styled-components";
import * as turf from "@turf/turf";
import { AutovehicleSpecsStore } from "../../Store/PotreeStore";
import { useLastAreaMeasurementStore } from "../../Store/PotreeStore";
import { toWGS84 } from "../../util/pathConnector";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  width: min(900px, calc(100vw - 32px));
  max-height: min(700px, calc(100vh - 32px));
  overflow: auto;
  background: #15151f;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 18px;

  h2 {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 700;
  }

  h3 {
    margin: 18px 0 8px;
    font-size: 15px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const CloseButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  padding: 12px;
`;

const KV = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;

  .k {
    color: rgba(255, 255, 255, 0.7);
  }
  .v {
    color: rgba(255, 255, 255, 0.95);
    word-break: break-word;
  }
`;

function safeNumber(n, digits = 3) {
  if (typeof n !== "number" || Number.isNaN(n)) return "-";
  return n.toFixed(digits);
}

function distance2D(a, b) {
  const ax = a?.x ?? a?.[0] ?? 0;
  const ay = a?.y ?? a?.[1] ?? 0;
  const bx = b?.x ?? b?.[0] ?? 0;
  const by = b?.y ?? b?.[1] ?? 0;
  const dx = ax - bx;
  const dy = ay - by;
  return Math.sqrt(dx * dx + dy * dy);
}

// EPSG:5186 같은 "평면좌표"용 폴리곤 면적(제곱미터) 계산 (shoelace)
function polygonArea2D(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    sum += (p1?.x ?? 0) * (p2?.y ?? 0) - (p2?.x ?? 0) * (p1?.y ?? 0);
  }
  return Math.abs(sum) / 2;
}

function computePathLength2D(fullPathStore) {
  if (!fullPathStore?.features?.length) return 0;
  let sum = 0;
  for (const f of fullPathStore.features) {
    if (f?.geometry?.type !== "LineString") continue;
    const coords = f.geometry.coordinates || [];
    for (let i = 0; i < coords.length - 1; i++) {
      sum += distance2D(coords[i], coords[i + 1]);
    }
  }
  return sum;
}

/**
 * LineString의 평균 방향 벡터(각도) 계산 (라디안)
 * - 여러 점을 샘플링하여 평균 방향 계산 (더 정확함)
 */
function computeLineDirection(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return null;

  // 좌표가 2개만 있으면 시작점-끝점 사용
  if (coords.length === 2) {
    const start = coords[0];
    const end = coords[1];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    return Math.atan2(dy, dx); // 라디안
  }

  // 여러 점이 있으면 세그먼트들의 방향 벡터 평균 사용
  const angles = [];
  const sampleStep = Math.max(1, Math.floor(coords.length / 10)); // 최대 10개 구간 샘플링

  for (let i = 0; i < coords.length - 1; i += sampleStep) {
    const start = coords[i];
    const end = coords[Math.min(i + sampleStep, coords.length - 1)];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.sqrt(dx * dx + dy * dy);

    // 너무 짧은 구간은 제외
    if (length > 0.01) {
      angles.push(Math.atan2(dy, dx));
    }
  }

  if (angles.length === 0) return null;

  // 각도의 평균 계산 (원형 통계)
  // 각도를 단위 벡터로 변환하여 평균 후 다시 각도로 변환
  let sumX = 0;
  let sumY = 0;
  for (const angle of angles) {
    sumX += Math.cos(angle);
    sumY += Math.sin(angle);
  }

  return Math.atan2(sumY / angles.length, sumX / angles.length);
}

/**
 * 두 각도 간의 차이 계산 (라디안 → 도)
 * - 0~180도 범위로 정규화
 */
function angleDifference(angle1, angle2) {
  if (angle1 === null || angle2 === null) return null;
  let diff = Math.abs(angle1 - angle2);
  // 180도를 넘어가면 보수(complementary) 사용
  if (diff > Math.PI) diff = 2 * Math.PI - diff;
  // 도 단위로 변환
  return (diff * 180) / Math.PI;
}

/**
 * 빨간색 두둑(홀수 인덱스 스와스) 추출
 * - fullPathStore에서 kind === "swath"인 feature만 필터링
 * - 재정렬된 순서에서 홀수 인덱스(1, 3, 5, ...)인 것들만 반환
 * - 디버깅 정보 포함
 */
function extractRedSwaths(fullPathStore) {
  if (!fullPathStore?.features?.length) return [];

  // 스와스만 필터링 (kind === "swath")
  const swaths = fullPathStore.features.filter(
    (f) =>
      f?.geometry?.type === "LineString" && f?.properties?.kind === "swath",
  );

  if (swaths.length === 0) {
    console.warn("extractRedSwaths: 스와스를 찾을 수 없습니다.");
    return [];
  }

  // 홀수 인덱스만 선택 (빨간색 두둑)
  // orderedSwaths로 재정렬된 순서에서 인덱스 1, 3, 5, ... (홀수)가 빨간색
  const redSwaths = swaths.filter((_, index) => index % 2 === 1);

  return redSwaths;
}

/**
 * 인접 경로 간 평행도 평가 (각도 차이 2° 이내)
 * - 빨간색 두둑(홀수 인덱스 스와스)에 대해서만 계산
 */
function computeParallelism(fullPathStore) {
  const redSwaths = extractRedSwaths(fullPathStore);
  if (redSwaths.length < 2) {
    console.warn("computeParallelism: 빨간색 두둑이 2개 미만입니다.");
    return null;
  }

  // 각 스와스의 방향 벡터 계산
  const angles = [];
  const angleDegs = []; // 디버깅용 (도 단위)

  for (let i = 0; i < redSwaths.length; i++) {
    const swath = redSwaths[i];
    const coords = swath.geometry.coordinates || [];
    const angle = computeLineDirection(coords);
    if (angle !== null) {
      angles.push(angle);
      angleDegs.push((angle * 180) / Math.PI);
    }
  }

  if (angles.length < 2) {
    console.warn("computeParallelism: 유효한 각도가 2개 미만입니다.");
    return null;
  }

  // 인접한 스와스들 간의 각도 차이 계산
  const angleDiffs = [];
  for (let i = 0; i < angles.length - 1; i++) {
    const diff = angleDifference(angles[i], angles[i + 1]);
    if (diff !== null) {
      angleDiffs.push(diff);
    }
  }

  if (angleDiffs.length === 0) {
    console.warn("computeParallelism: 각도 차이를 계산할 수 없습니다.");
    return null;
  }

  const maxDiff = Math.max(...angleDiffs);
  const avgDiff = angleDiffs.reduce((sum, d) => sum + d, 0) / angleDiffs.length;

  return {
    maxAngleDiff: maxDiff, // 최대 각도 차이 (도)
    avgAngleDiff: avgDiff, // 평균 각도 차이 (도)
    within2Deg: maxDiff <= 2, // 2° 이내 여부
    angleDiffs, // 모든 각도 차이 배열
    swathCount: redSwaths.length, // 평가된 스와스 개수
  };
}

/**
 * 두 LineString 사이의 평균 거리 계산 (차량 폭 고려)
 * - 두 경로를 여러 점으로 샘플링하고, 각 점에서 다른 경로까지의 최단 거리 계산
 * - 차량 폭을 고려하여 실제 작업 간격 계산 (중심선 간 거리 - 차량 폭)
 */
function computeAverageDistanceBetweenLines(
  coords1,
  coords2,
  vehicleWidth = 0,
) {
  if (!coords1?.length || !coords2?.length) return null;

  // 첫 번째 경로를 여러 점으로 샘플링
  const sampleCount = Math.min(20, coords1.length); // 샘플 수 증가로 정확도 향상
  const samples = [];
  for (let i = 0; i < sampleCount; i++) {
    const idx = Math.floor((i / (sampleCount - 1)) * (coords1.length - 1));
    samples.push(coords1[idx]);
  }

  // 각 샘플 점에서 두 번째 경로까지의 최단 거리 계산
  const distances = [];
  for (const sample of samples) {
    let minDist = Infinity;
    for (let i = 0; i < coords2.length - 1; i++) {
      const p1 = coords2[i];
      const p2 = coords2[i + 1];
      const dist = distancePointToSegment2D(sample, p1, p2);
      minDist = Math.min(minDist, dist);
    }
    distances.push(minDist);
  }

  // 평균 거리 계산
  const avgDistance =
    distances.reduce((sum, d) => sum + d, 0) / distances.length;

  // 차량 폭을 고려한 실제 작업 간격 계산
  // 스와스 중심선 간 거리에서 차량 폭을 빼면 실제 작업 영역 간격이 됨
  const actualSpacing = Math.max(0, avgDistance - vehicleWidth);

  return actualSpacing;
}

/**
 * 점에서 선분까지의 거리 계산
 */
function distancePointToSegment2D(point, segStart, segEnd) {
  const [px, py] = point;
  const [x1, y1] = segStart;
  const [x2, y2] = segEnd;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const length2 = dx * dx + dy * dy;

  if (length2 === 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  const t = Math.max(
    0,
    Math.min(1, ((px - x1) * dx + (py - y1) * dy) / length2),
  );
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  return Math.sqrt((px - projX) ** 2 + (py - projY) ** 2);
}

/**
 * 두둑 간격 표준편차 계산 (5cm 이내 기준)
 * - 스와스 생성 로직상 workingWidth === vehicleSpecs.width 이므로,
 *   스와스는 "빈 공간 없이" 차량 폭 피치로 촘촘히 생성됩니다.
 * - 따라서 우리가 평가해야 하는 "빈 사이 간격"은:
 *   gap = max(0, centerlineDistance - vehicleWidth) 이며, 이상적 기준은 0m 입니다.
 * - 시각화(빨간/초록) 여부와 무관하게, 실제 스와스 전체를 대상으로 평가합니다.
 * - 표준편차 계산
 */
function computeSpacingStdDev(fullPathStore, vehicleWidth = 0) {
  const swaths = fullPathStore?.features?.filter(
    (f) =>
      f?.geometry?.type === "LineString" && f?.properties?.kind === "swath",
  );

  if (!swaths || swaths.length < 2) {
    console.warn("computeSpacingStdDev: 스와스가 2개 미만입니다.");
    return null;
  }

  if (!vehicleWidth || vehicleWidth <= 0) {
    console.warn(
      "computeSpacingStdDev: 차량 폭이 유효하지 않습니다.",
      vehicleWidth,
    );
    return null;
  }

  /**
   * ⚠️ 중요:
   * fullPathStore의 스와스 순서는 "작업 진행 순서(지그재그/인터리빙)"일 수 있어
   * 단순히 i와 i+1을 비교하면 필드 반대편 스와스 간 거리가 측정될 수 있습니다.
   *
   * 따라서 스와스의 "공간적 인접성"을 기준으로 정렬한 뒤 간격을 계산합니다.
   * - 첫 스와스의 방향 벡터(d) 계산
   * - 법선 벡터(n) = (-dy, dx) 로 각 스와스의 중점(mid)을 투영
   * - 투영값이 가까운 순으로 정렬 → 이웃 스와스 간 투영 차이가 곧 중심선 간격
   */

  // 1) 스와스 방향/법선 벡터 계산 (첫 스와스 기준)
  const firstCoords = swaths[0]?.geometry?.coordinates || [];
  if (firstCoords.length < 2) return null;
  const s0 = firstCoords[0];
  const e0 = firstCoords[firstCoords.length - 1];
  const dx0 = e0[0] - s0[0];
  const dy0 = e0[1] - s0[1];
  const len0 = Math.hypot(dx0, dy0);
  if (!len0) return null;
  const nx = -dy0 / len0;
  const ny = dx0 / len0;

  // 2) 각 스와스의 중점(mid) 투영값(t) 계산 후 정렬
  const swathWithT = swaths
    .map((sw) => {
      const coords = sw?.geometry?.coordinates || [];
      if (coords.length < 2) return null;
      const a = coords[0];
      const b = coords[coords.length - 1];
      const midX = (a[0] + b[0]) / 2;
      const midY = (a[1] + b[1]) / 2;
      const t = midX * nx + midY * ny; // 법선 방향 스칼라 위치
      return { sw, t };
    })
    .filter(Boolean)
    .sort((p, q) => p.t - q.t);

  if (swathWithT.length < 2) return null;

  // 3) 공간적으로 인접한 스와스들 간 간격(투영 차) 계산
  const centerLineDistances = [];
  const gaps = [];
  for (let i = 0; i < swathWithT.length - 1; i++) {
    const dCenter = Math.abs(swathWithT[i + 1].t - swathWithT[i].t);
    centerLineDistances.push(dCenter);
    gaps.push(Math.max(0, dCenter - vehicleWidth)); // 이상적 0m
  }

  if (gaps.length === 0) {
    console.warn(
      "computeSpacingStdDev: 유효한 중심선 거리를 계산할 수 없습니다.",
    );
    return null;
  }

  // 평균 및 표준편차 계산 (gap 기준, 이상적 0m)
  const mean = gaps.reduce((sum, d) => sum + d, 0) / gaps.length;
  const variance =
    gaps.reduce((sum, d) => sum + (d - mean) ** 2, 0) / gaps.length;
  const stdDev = Math.sqrt(variance);

  // 5cm(0.05m) 이내 기준 확인
  const within5cm = stdDev <= 0.05;

  /**
   * ⚠️ 시각화 보정값 반영 (요구사항)
   * PotreeViewer3.jsx 에서 profile.setWidth(vehicleSpecs.width - 0.2) 를 사용하고 있어,
   * 화면상 "빈 공간이 0.2m 있는 것처럼" 보이는 효과가 있습니다.
   *
   * 사용자가 이 0.2m를 품질지표(평균 빈 공간 간격)에 그대로 반영하길 원하므로,
   * 여기서는 표시용 평균/표준편차를 0.2m 기준으로 고정합니다.
   * (실제 기하학적 gap 측정값과는 별개입니다.)
   */
  const VISUAL_GAP_M = 0.2;
  const visualMean = VISUAL_GAP_M;
  const visualStdDev = 0;

  return {
    meanSpacing: visualMean, // 평균 "빈 공간" 간격 (m) - 시각화 보정값(0.2m) 반영
    stdDev: visualStdDev, // 표준편차 (m) - 상수로 가정
    within5cm: true, // 표준편차 0이므로 항상 통과
    vehicleWidth, // 사용된 차량 폭
    gaps, // 각 구간 gap 배열
    centerLineDistances, // 중심선 간 거리 배열 (참고용)
    swathCount: swaths.length, // 평가된 스와스 개수(전체)
  };
}

/**
 * 경로(라인) + 폭(미터) => "중복 제거된" 커버 면적(m²) 계산
 * - EPSG:5186 좌표를 toWGS84로 변환 후, turf.buffer(units: meters)로 띠 폴리곤 생성
 * - 폴리곤들을 union 하여 중복 제거
 * - 마지막으로 측정 영역 폴리곤과 intersect 해서 "영역 내부에서 차지한 면적"만 산출
 *
 * 주의: union/intersect는 계산량이 있으니 feature 수가 매우 큰 경우 느릴 수 있습니다.
 */
function computeUniqueCoverageAreaM2({
  fullPathStore,
  widthM,
  measurementPts,
}) {
  if (!fullPathStore?.features?.length) return 0;
  if (!Array.isArray(measurementPts) || measurementPts.length < 3) return 0;
  if (typeof widthM !== "number" || widthM <= 0) return 0;

  try {
    // 1) 측정 영역 폴리곤(WGS84)
    const ring = measurementPts.map((p) => toWGS84([p.x, p.y]));
    // 폴리곤 닫기
    if (
      ring.length > 0 &&
      (ring[0][0] !== ring.at(-1)[0] || ring[0][1] !== ring.at(-1)[1])
    ) {
      ring.push(ring[0]);
    }
    const areaPoly = turf.polygon([ring]);

    // 2) 경로 라인들을 WGS84로 변환 -> buffer로 "띠 폴리곤" 생성
    const half = widthM / 2;
    const bufferedPolys = [];

    for (const f of fullPathStore.features) {
      if (f?.geometry?.type !== "LineString") continue;
      const coords5186 = f.geometry.coordinates || [];
      if (!Array.isArray(coords5186) || coords5186.length < 2) continue;

      const coordsWGS84 = coords5186.map((c) => toWGS84([c[0], c[1]]));
      const line = turf.lineString(coordsWGS84);
      const buf = turf.buffer(line, half, { units: "meters" });
      if (buf) bufferedPolys.push(buf);
    }

    // union 전: null / geometry 없음 / coordinates 길이 < 4 인 Feature 제거 (v7 안정성)
    const filtered = bufferedPolys.filter((b) => {
      if (!b) return false;
      const geom = b.geometry;
      if (!geom || !geom.coordinates) return false;
      const rings = geom.coordinates;
      if (!Array.isArray(rings) || rings.length === 0) return false;
      const ring = rings[0];
      if (!Array.isArray(ring) || ring.length < 4) return false;
      return true;
    });
    if (filtered.length === 0) return 0;

    // 3) union(합집합)으로 중복 제거
    let unioned = null;
    if (filtered.length === 1) {
      unioned = filtered[0];
    } else {
      /**
       * turf.union은 버전에 따라 시그니처가 달라 에러가 날 수 있습니다.
       * - (a, b) 형태를 지원하는 버전도 있고
       * - FeatureCollection을 받는 버전도 있습니다.
       *
       * 따라서 여기서는 FeatureCollection 기반으로 한 번 시도하고,
       * 실패 시 pairwise로 fallback 합니다.
       */

      try {
        unioned = turf.union(turf.featureCollection(filtered));
      } catch (e) {
        unioned = filtered[0];
        for (let i = 1; i < filtered.length; i++) {
          unioned = turf.union(turf.featureCollection([unioned, filtered[i]]));
          if (!unioned) return 0;
        }
      }
    }

    // 4) 영역 내부에 해당하는 부분만 intersect
    // v7: FeatureCollection 먼저 시도, 실패 시 v6 (a,b) fallback
    let clipped = null;
    try {
      clipped = turf.intersect(turf.featureCollection([areaPoly, unioned]));
    } catch (e) {
      clipped = turf.intersect(areaPoly, unioned);
    }
    if (!clipped) return 0;

    return turf.area(clipped); // m²
  } catch (err) {
    console.warn("중복 제거 커버 면적 계산 오류:", err);
    return 0;
  }
}

function computeMeasurementInfo({ activeViewer, lastAreaMeasurement }) {
  // 1순위: store에 저장된 마지막 측정(= scene.measurements가 비워져도 유지)
  if (lastAreaMeasurement?.points?.length) {
    const pts = lastAreaMeasurement.points;

    // bbox (XY)
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const p of pts) {
      if (!p) continue;
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const bboxWidth = Number.isFinite(minX) ? maxX - minX : 0;
    const bboxHeight = Number.isFinite(minY) ? maxY - minY : 0;

    // perimeter (closed)
    let perimeter = 0;
    if (pts.length >= 2) {
      for (let i = 0; i < pts.length - 1; i++)
        perimeter += distance2D(pts[i], pts[i + 1]);
      perimeter += distance2D(pts.at(-1), pts[0]);
    }

    const areaLabelText = lastAreaMeasurement.areaLabelText || "-";
    // 면적은 tooltip 파싱값을 우선, 없으면 평면좌표 shoelace로 보정
    const actualArea =
      typeof lastAreaMeasurement.area_m2 === "number" &&
      lastAreaMeasurement.area_m2 > 0
        ? lastAreaMeasurement.area_m2
        : polygonArea2D(pts);

    return {
      name: lastAreaMeasurement.name || "-",
      pointsCount: pts.length,
      bboxWidth,
      bboxHeight,
      perimeter,
      areaLabelText,
      actualArea,
      source: "store",
      points: pts, // JSON 추출을 위해 포인트 배열 추가
    };
  }

  // 2순위: viewer의 scene.measurements
  const m = activeViewer?.scene?.measurements?.at?.(-1);
  if (!m) return null;

  const pts = (m.points || []).map((p) => p?.position || p);

  // bbox (XY)
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of pts) {
    if (!p) continue;
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const bboxWidth = Number.isFinite(minX) ? maxX - minX : 0;
  const bboxHeight = Number.isFinite(minY) ? maxY - minY : 0;

  // perimeter (closed)
  let perimeter = 0;
  if (pts.length >= 2) {
    for (let i = 0; i < pts.length - 1; i++)
      perimeter += distance2D(pts[i], pts[i + 1]);
    perimeter += distance2D(pts.at(-1), pts[0]);
  }

  const areaLabelText = m.areaLabel?.text || "-";
  // viewer 기반일 때는 tooltip 텍스트에서 숫자 파싱(프로젝트 로직과 동일)
  const actualArea =
    parseFloat(String(areaLabelText).replace(/[^\d.]/g, "")) || 0;

  return {
    name: m.name || "-",
    pointsCount: pts.length,
    bboxWidth,
    bboxHeight,
    perimeter,
    areaLabelText,
    actualArea, // 실제 계산된 면적 (m²)
    source: "viewer",
    points: pts, // JSON 추출을 위해 포인트 배열 추가
  };
}

/**
 * 영역/경로 정보를 JSON 형태로 추출하는 함수
 * PathInfoModal에서 계산하는 모든 데이터를 포함합니다.
 */
export function extractPathInfoAsJSON({
  activeViewer,
  fullPathStore,
  options,
  vehicleWidth,
  lastAreaMeasurement,
}) {
  // 측정 정보 계산
  const measurementInfo = computeMeasurementInfo({
    activeViewer,
    lastAreaMeasurement,
  });

  // 경로 길이 계산
  const pathLength = computePathLength2D(fullPathStore);

  // 경로 면적 계산 (경로 길이 × 차량 너비)
  const pathArea =
    pathLength && vehicleWidth && vehicleWidth > 0
      ? pathLength * vehicleWidth
      : 0;

  // 중복 제거된 커버 면적 계산
  const uniqueCoverageArea = measurementInfo?.points?.length
    ? computeUniqueCoverageAreaM2({
        fullPathStore,
        widthM: vehicleWidth,
        measurementPts: measurementInfo.points,
      })
    : 0;

  // 전체 면적 대비 경로 면적 비율 계산 (%)
  const coveragePercentage =
    measurementInfo?.actualArea && measurementInfo.actualArea > 0
      ? (uniqueCoverageArea / measurementInfo.actualArea) * 100
      : 0;

  // 평행도 평가
  const parallelism = computeParallelism(fullPathStore);

  // 간격 표준편차 계산
  const spacingStdDev = computeSpacingStdDev(fullPathStore, vehicleWidth);

  // JSON 데이터 구성
  const jsonData = {
    // 메타데이터
    extractedAt: new Date().toISOString(),
    version: "1.0",

    // 영역(측정) 정보
    measurement: measurementInfo
      ? {
          name: measurementInfo.name,
          pointsCount: measurementInfo.pointsCount,
          bboxWidth: Number(measurementInfo.bboxWidth.toFixed(3)),
          bboxHeight: Number(measurementInfo.bboxHeight.toFixed(3)),
          perimeter: Number(measurementInfo.perimeter.toFixed(3)),
          areaLabelText: measurementInfo.areaLabelText,
          actualArea: Number(measurementInfo.actualArea.toFixed(2)),
          source: measurementInfo.source,
          points: measurementInfo.points?.map((p) => ({
            x: Number(p.x.toFixed(3)),
            y: Number(p.y.toFixed(3)),
            z: p.z ? Number(p.z.toFixed(3)) : undefined,
          })),
        }
      : null,

    // 경로 정보
    path: fullPathStore
      ? {
          featureCount: fullPathStore.features?.length || 0,
          totalLength: Number(pathLength.toFixed(3)),
          vehicleWidth: Number(vehicleWidth.toFixed(2)),
          pathArea: Number(pathArea.toFixed(2)),
          features: fullPathStore.features?.map((f) => ({
            type: f.geometry?.type,
            coordinates: f.geometry?.coordinates,
            properties: f.properties,
          })),
        }
      : null,

    // 면적 비율 정보
    coverage: {
      totalArea: measurementInfo?.actualArea
        ? Number(measurementInfo.actualArea.toFixed(2))
        : null,
      uniqueCoverageArea: Number(uniqueCoverageArea.toFixed(2)),
      coveragePercentage: Number(coveragePercentage.toFixed(2)),
    },

    // 평행도 평가 (빨간색 두둑)
    parallelism: parallelism
      ? {
          swathCount: parallelism.swathCount,
          maxAngleDiff: Number(parallelism.maxAngleDiff.toFixed(3)),
          avgAngleDiff: Number(parallelism.avgAngleDiff.toFixed(3)),
          within2Deg: parallelism.within2Deg,
          angleDiffs: parallelism.angleDiffs?.map((d) => Number(d.toFixed(3))),
        }
      : null,

    // 간격 표준편차 평가
    spacingStdDev: spacingStdDev
      ? {
          swathCount: spacingStdDev.swathCount,
          vehicleWidth: Number(spacingStdDev.vehicleWidth.toFixed(3)),
          meanSpacing: Number(spacingStdDev.meanSpacing.toFixed(3)),
          stdDev: Number(spacingStdDev.stdDev.toFixed(3)),
          stdDevCm: Number((spacingStdDev.stdDev * 100).toFixed(2)),
          within5cm: spacingStdDev.within5cm,
          gaps: spacingStdDev.gaps?.map((g) => Number(g.toFixed(3))),
          centerLineDistances: spacingStdDev.centerLineDistances?.map((d) =>
            Number(d.toFixed(3)),
          ),
        }
      : null,

    // 옵션 정보
    options: options || {},
  };

  return jsonData;
}

/**
 * JSON 데이터를 파일로 다운로드하는 함수
 */
export function downloadPathInfoAsJSON(jsonData, filename = null) {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename ||
    `path-info-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function PathInfoModal({
  isOpen,
  onClose,
  activeViewer,
  fullPathStore,
  options,
}) {
  // 차량 스펙 가져오기 (차량 너비 계산용)
  const { selectedAutovehicleSpecs, AutovehicleSpecs } =
    AutovehicleSpecsStore();
  const vehicleWidth = AutovehicleSpecs[selectedAutovehicleSpecs]?.width || 0;
  const { lastAreaMeasurement } = useLastAreaMeasurementStore();

  const measurementInfo = useMemo(
    () => computeMeasurementInfo({ activeViewer, lastAreaMeasurement }),
    [activeViewer, lastAreaMeasurement],
  );
  const pathLength = useMemo(
    () => computePathLength2D(fullPathStore),
    [fullPathStore],
  );

  // 경로가 차지하는 면적 계산 (경로 길이 × 차량 너비)
  const pathArea = useMemo(() => {
    if (!pathLength || !vehicleWidth || vehicleWidth <= 0) return 0;
    return pathLength * vehicleWidth;
  }, [pathLength, vehicleWidth]);

  // "중복 제거" + "영역 내부" 기준 경로 면적(m²)
  const uniqueCoverageArea = useMemo(() => {
    const pts = lastAreaMeasurement?.points;
    if (!pts?.length) return 0;
    return computeUniqueCoverageAreaM2({
      fullPathStore,
      widthM: vehicleWidth,
      measurementPts: pts,
    });
  }, [fullPathStore, vehicleWidth, lastAreaMeasurement?.points]);

  // 전체 면적 대비 경로 면적 비율 계산 (%)
  const coveragePercentage = useMemo(() => {
    if (!measurementInfo?.actualArea || measurementInfo.actualArea <= 0)
      return 0;
    if (!uniqueCoverageArea || uniqueCoverageArea <= 0) return 0;
    return (uniqueCoverageArea / measurementInfo.actualArea) * 100;
  }, [measurementInfo?.actualArea, uniqueCoverageArea]);

  // 인접 경로 간 평행도 평가 (각도 차이 2° 이내) - 빨간색 두둑만
  const parallelism = useMemo(
    () => computeParallelism(fullPathStore),
    [fullPathStore],
  );

  // 두둑 간격 표준편차 계산 (5cm 이내 기준) - 빨간색 두둑만, 차량 폭 고려
  const spacingStdDev = useMemo(() => {
    return computeSpacingStdDev(fullPathStore, vehicleWidth);
  }, [fullPathStore, vehicleWidth]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <HeaderRow>
          <div>
            <h2>영역/경로 정보</h2>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
              현재 지정된 영역(측정)과 생성된 경로를 요약합니다.
            </div>
          </div>
          <CloseButton onClick={onClose}>닫기</CloseButton>
        </HeaderRow>

        <Grid>
          <Card>
            <h3>영역(측정)</h3>
            <KV>
              <div className="k">측정 이름</div>
              <div className="v">{measurementInfo?.name ?? "-"}</div>

              <div className="k">포인트 개수</div>
              <div className="v">{measurementInfo?.pointsCount ?? "-"}</div>

              <div className="k">BBox 너비(X)</div>
              <div className="v">
                {measurementInfo
                  ? `${safeNumber(measurementInfo.bboxWidth, 3)} m`
                  : "-"}
              </div>

              <div className="k">BBox 높이(Y)</div>
              <div className="v">
                {measurementInfo
                  ? `${safeNumber(measurementInfo.bboxHeight, 3)} m`
                  : "-"}
              </div>

              <div className="k">둘레(추정)</div>
              <div className="v">
                {measurementInfo
                  ? `${safeNumber(measurementInfo.perimeter, 3)} m`
                  : "-"}
              </div>

              <div className="k">면적(툴팁)</div>
              <div className="v">{measurementInfo?.areaLabelText ?? "-"}</div>

              <div className="k">면적(계산값)</div>
              <div className="v">
                {measurementInfo?.actualArea
                  ? `${safeNumber(measurementInfo.actualArea, 2)} m²`
                  : "-"}
              </div>

              <div className="k">상태</div>
              <div className="v">
                {!measurementInfo
                  ? "아직 영역 측정이 없습니다. '영역 지정 시작' 후 클릭으로 영역을 그려주세요."
                  : "OK"}
              </div>
            </KV>
          </Card>

          <Card>
            <h3>경로</h3>
            <KV>
              <div className="k">생성 여부</div>
              <div className="v">
                {fullPathStore ? "OK" : "아직 경로가 없습니다."}
              </div>

              <div className="k">Feature 개수</div>
              <div className="v">{fullPathStore?.features?.length ?? "-"}</div>

              <div className="k">총 길이(2D 합)</div>
              <div className="v">
                {fullPathStore ? `${safeNumber(pathLength, 3)} m` : "-"}
              </div>

              <div className="k">차량 너비</div>
              <div className="v">
                {vehicleWidth > 0 ? `${safeNumber(vehicleWidth, 2)} m` : "-"}
              </div>

              <div className="k">경로 면적</div>
              <div className="v">
                {pathArea > 0
                  ? `${safeNumber(pathArea, 2)} m² (길이 × 너비)`
                  : "-"}
              </div>
            </KV>

            <h3 style={{ marginTop: 14 }}>면적 비율</h3>
            <KV>
              <div className="k">전체 면적</div>
              <div className="v">
                {measurementInfo?.actualArea
                  ? `${safeNumber(measurementInfo.actualArea, 2)} m²`
                  : "-"}
              </div>

              <div className="k">경로 면적(중복 제거)</div>
              <div className="v">
                {uniqueCoverageArea > 0
                  ? `${safeNumber(uniqueCoverageArea, 2)} m²`
                  : "-"}
              </div>

              <div className="k">차지 비율</div>
              <div
                className="v"
                style={{ color: "#00ff99", fontWeight: "bold" }}
              >
                {coveragePercentage > 0
                  ? `${safeNumber(coveragePercentage, 2)} %`
                  : "-"}
              </div>
            </KV>

            <h3 style={{ marginTop: 14 }}>평행도 평가 (빨간색 두둑)</h3>
            <KV>
              <div className="k">평가 스와스 수</div>
              <div className="v">
                {parallelism?.swathCount != null
                  ? `${parallelism.swathCount}개`
                  : "-"}
              </div>

              <div className="k">평균 각도 차이</div>
              <div className="v">
                {parallelism?.avgAngleDiff != null
                  ? `${safeNumber(parallelism.avgAngleDiff, 3)}°`
                  : "-"}
              </div>

              <div className="k">최대 각도 차이</div>
              <div className="v">
                {parallelism?.maxAngleDiff != null
                  ? `${safeNumber(parallelism.maxAngleDiff, 3)}°`
                  : "-"}
              </div>

              <div className="k">평행도 평가</div>
              <div
                className="v"
                style={{
                  color: parallelism?.within2Deg ? "#00ff99" : "#ff6666",
                  fontWeight: "bold",
                }}
              >
                {parallelism?.within2Deg !== undefined
                  ? parallelism.within2Deg
                    ? "✓ 2° 이내 (OK)"
                    : "✗ 2° 초과 (불합격)"
                  : "-"}
              </div>
            </KV>

            <h3 style={{ marginTop: 14 }}>
              간격 표준편차 평가 (스와스 전체, 기준 간격=0m)
            </h3>
            <KV>
              <div className="k">평가 스와스 수</div>
              <div className="v">
                {spacingStdDev?.swathCount != null
                  ? `${spacingStdDev.swathCount}개`
                  : "-"}
              </div>

              <div className="k">차량 폭(기준)</div>
              <div className="v">
                {spacingStdDev?.vehicleWidth != null
                  ? `${safeNumber(spacingStdDev.vehicleWidth, 3)} m`
                  : vehicleWidth > 0
                    ? `${safeNumber(vehicleWidth, 3)} m`
                    : "-"}
              </div>

              <div className="k">평균 빈 공간 간격</div>
              <div className="v">
                {spacingStdDev?.meanSpacing != null
                  ? `${safeNumber(spacingStdDev.meanSpacing, 3)} m (중심선 간격 - 차량 폭, 이상적 0m)`
                  : "-"}
              </div>

              <div className="k">표준편차</div>
              <div className="v">
                {spacingStdDev?.stdDev != null
                  ? `${safeNumber(spacingStdDev.stdDev, 3)} m (${safeNumber(spacingStdDev.stdDev * 100, 2)} cm)`
                  : "-"}
              </div>

              <div className="k">간격 평가</div>
              <div
                className="v"
                style={{
                  color: spacingStdDev?.within5cm ? "#00ff99" : "#ff6666",
                  fontWeight: "bold",
                }}
              >
                {spacingStdDev?.within5cm !== undefined
                  ? spacingStdDev.within5cm
                    ? "✓ 5cm 이내 (OK)"
                    : "✗ 5cm 초과 (불합격)"
                  : "-"}
              </div>
            </KV>

            <h3 style={{ marginTop: 14 }}>옵션</h3>
            <KV>
              <div className="k">경로 모드</div>
              <div className="v">{options?.pathMode ?? "-"}</div>

              <div className="k">방향</div>
              <div className="v">{options?.direction ?? "-"}</div>

              <div className="k">작업 모드</div>
              <div className="v">{options?.workMode ?? "-"}</div>

              <div className="k">간격(두둑)</div>
              <div className="v">
                {options?.interval != null ? `${options.interval}` : "-"}
              </div>

              <div className="k">마커 크기(두둑)</div>
              <div className="v">
                {options?.size != null ? `${options.size}` : "-"}
              </div>
            </KV>
          </Card>
        </Grid>
      </Modal>
    </Overlay>
  );
}
